/**
 * Service de monitoring pour l'authentification
 * Trace tous les événements d'authentification pour analyse et debugging
 */

import { logger } from '../../utils/debug/logger.js';

// Types d'événements d'authentification
export const AUTH_EVENT_TYPES = {
    INIT_START: 'auth.init.start',
    INIT_SUCCESS: 'auth.init.success',
    INIT_FAILURE: 'auth.init.failure',
    PARTNER_AUTH_START: 'auth.partner.start',
    PARTNER_AUTH_SUCCESS: 'auth.partner.success',
    PARTNER_AUTH_FAILURE: 'auth.partner.failure',
    USER_AUTH_START: 'auth.user.start',
    USER_AUTH_SUCCESS: 'auth.user.success',
    USER_AUTH_FAILURE: 'auth.user.failure',
    VALIDATION_ERROR: 'auth.validation.error',
    API_ERROR: 'auth.api.error',
    RACE_CONDITION_DETECTED: 'auth.race_condition.detected'
};

// Stockage en mémoire des événements (limité aux 100 derniers)
const authEvents = [];
const MAX_EVENTS = 100;

// Métriques agrégées
const metrics = {
    totalAttempts: 0,
    totalSuccesses: 0,
    totalFailures: 0,
    byMode: {
        iframe: { attempts: 0, successes: 0, failures: 0 },
        proxy: { attempts: 0, successes: 0, failures: 0 },
        direct: { attempts: 0, successes: 0, failures: 0 }
    },
    byType: {
        partner: { attempts: 0, successes: 0, failures: 0 },
        user: { attempts: 0, successes: 0, failures: 0 }
    },
    validationErrors: 0,
    apiErrors: 0,
    raceConditions: 0
};

/**
 * Enregistre un événement d'authentification
 * @param {string} type - Type d'événement (AUTH_EVENT_TYPES)
 * @param {Object} data - Données associées à l'événement
 */
export function trackAuthEvent(type, data = {}) {
    const timestamp = new Date().toISOString();
    const event = {
        type,
        timestamp,
        data: {
            ...data,
            // Masquer les données sensibles dans les logs
            token: data.token ? '***MASKED***' : undefined,
            partnerToken: data.partnerToken ? '***MASKED***' : undefined
        }
    };

    // Ajouter à l'historique (limite à MAX_EVENTS)
    authEvents.push(event);
    if (authEvents.length > MAX_EVENTS) {
        authEvents.shift(); // Retirer le plus ancien
    }

    // Mettre à jour les métriques
    updateMetrics(type, data);

    // Logger l'événement selon son importance
    logEvent(type, event);
}

/**
 * Met à jour les métriques agrégées
 * @private
 */
function updateMetrics(type, data) {
    const mode = data.mode || 'unknown';

    switch (type) {
        case AUTH_EVENT_TYPES.INIT_START:
            metrics.totalAttempts++;
            if (metrics.byMode[mode]) {
                metrics.byMode[mode].attempts++;
            }
            break;

        case AUTH_EVENT_TYPES.INIT_SUCCESS:
            metrics.totalSuccesses++;
            if (metrics.byMode[mode]) {
                metrics.byMode[mode].successes++;
            }
            break;

        case AUTH_EVENT_TYPES.INIT_FAILURE:
            metrics.totalFailures++;
            if (metrics.byMode[mode]) {
                metrics.byMode[mode].failures++;
            }
            break;

        case AUTH_EVENT_TYPES.PARTNER_AUTH_START:
            metrics.byType.partner.attempts++;
            break;

        case AUTH_EVENT_TYPES.PARTNER_AUTH_SUCCESS:
            metrics.byType.partner.successes++;
            break;

        case AUTH_EVENT_TYPES.PARTNER_AUTH_FAILURE:
            metrics.byType.partner.failures++;
            break;

        case AUTH_EVENT_TYPES.USER_AUTH_START:
            metrics.byType.user.attempts++;
            break;

        case AUTH_EVENT_TYPES.USER_AUTH_SUCCESS:
            metrics.byType.user.successes++;
            break;

        case AUTH_EVENT_TYPES.USER_AUTH_FAILURE:
            metrics.byType.user.failures++;
            break;

        case AUTH_EVENT_TYPES.VALIDATION_ERROR:
            metrics.validationErrors++;
            break;

        case AUTH_EVENT_TYPES.API_ERROR:
            metrics.apiErrors++;
            break;

        case AUTH_EVENT_TYPES.RACE_CONDITION_DETECTED:
            metrics.raceConditions++;
            break;
    }
}

/**
 * Logger l'événement avec le niveau approprié
 * @private
 */
function logEvent(type, event) {
    const isFailure = type.includes('failure') || type.includes('error');
    const isWarning = type.includes('race_condition');

    if (isFailure) {
        logger.auth.error(`🔴 ${type}`, event.data);
    } else if (isWarning) {
        logger.auth.warn(`🟡 ${type}`, event.data);
    } else {
        logger.auth.debug(`🟢 ${type}`, event.data);
    }
}

/**
 * Récupère l'historique des événements d'authentification
 * @param {number} limit - Nombre max d'événements à retourner
 * @returns {Array} Liste des événements
 */
export function getAuthHistory(limit = 50) {
    return authEvents.slice(-limit);
}

/**
 * Récupère les métriques d'authentification
 * @returns {Object} Métriques agrégées
 */
export function getAuthMetrics() {
    // Calculer les taux de succès
    const successRate = metrics.totalAttempts > 0
        ? ((metrics.totalSuccesses / metrics.totalAttempts) * 100).toFixed(2)
        : 0;

    const partnerSuccessRate = metrics.byType.partner.attempts > 0
        ? ((metrics.byType.partner.successes / metrics.byType.partner.attempts) * 100).toFixed(2)
        : 0;

    const userSuccessRate = metrics.byType.user.attempts > 0
        ? ((metrics.byType.user.successes / metrics.byType.user.attempts) * 100).toFixed(2)
        : 0;

    return {
        ...metrics,
        successRate: `${successRate}%`,
        partnerSuccessRate: `${partnerSuccessRate}%`,
        userSuccessRate: `${userSuccessRate}%`
    };
}

/**
 * Réinitialise les métriques (utile pour les tests)
 */
export function resetAuthMetrics() {
    metrics.totalAttempts = 0;
    metrics.totalSuccesses = 0;
    metrics.totalFailures = 0;
    metrics.byMode = {
        iframe: { attempts: 0, successes: 0, failures: 0 },
        proxy: { attempts: 0, successes: 0, failures: 0 },
        direct: { attempts: 0, successes: 0, failures: 0 }
    };
    metrics.byType = {
        partner: { attempts: 0, successes: 0, failures: 0 },
        user: { attempts: 0, successes: 0, failures: 0 }
    };
    metrics.validationErrors = 0;
    metrics.apiErrors = 0;
    metrics.raceConditions = 0;

    authEvents.length = 0;

    logger.auth.debug('📊 Authentication metrics reset');
}

/**
 * Affiche un rapport des métriques dans la console
 */
export function printAuthReport() {
    const metricsData = getAuthMetrics();

    console.group('📊 Authentication Metrics Report');
    console.log('Total Attempts:', metricsData.totalAttempts);
    console.log('Total Successes:', metricsData.totalSuccesses);
    console.log('Total Failures:', metricsData.totalFailures);
    console.log('Success Rate:', metricsData.successRate);
    console.log('');
    console.log('By Mode:');
    console.table(metricsData.byMode);
    console.log('By Type:');
    console.table(metricsData.byType);
    console.log('');
    console.log('Issues:');
    console.log('  Validation Errors:', metricsData.validationErrors);
    console.log('  API Errors:', metricsData.apiErrors);
    console.log('  Race Conditions:', metricsData.raceConditions);
    console.groupEnd();
}

/**
 * Expose les fonctions de monitoring globalement pour debugging
 */
if (typeof window !== 'undefined') {
    window.authMonitoring = {
        getHistory: getAuthHistory,
        getMetrics: getAuthMetrics,
        printReport: printAuthReport,
        reset: resetAuthMetrics
    };

    logger.auth.debug('🔍 Auth monitoring available via window.authMonitoring');
}
